import axios from 'axios';
import _ from 'lodash';
import { store } from '@/scripts/store';
import {
  isArray, getQcloudSign, getQcloudApiCommonParams, mergeData, queryParamsToString,
} from '@/scripts/tools/utils';
import { V4RegionMap } from '@/scripts/tools/config';
import cosAuth from '@/plugins/cos_sdk/src/cos-auth';
import eventProxy from '@/scripts/tools/eventProxy';
import cos from './cos/index';
import user from './user';

axios.defaults.timeout = 15000;
// axios.defaults.baseURL = window.location.baseURL;
axios.defaults.baseURL = store.cosbrowserServerUrl;

// access-rules 中间件下发的拒绝标记。命中后由 App.vue 监听 'access-blocked' 事件统一处理
// （登出 + 提示），避免业务方各自零散弹窗
const ACCESS_BLOCKED_ERR_CODES = new Set(['ACCESS_BLOCKED', 'LONGTERM_KEY_DISABLED']);
// OAuth binding 校验失败：发事件交 App.vue 处理（登出 + 跳登录页 + 提示重登）
// 当次失败 reject 给业务（保持原有错误链路，避免 swallow 错误隐藏问题）
const OAUTH_BINDING_INVALID = 'OAUTH_BINDING_INVALID';
// AK 白名单登录 ticket cookie 缺失/失效：与 OAuth binding 类似，交 App.vue 统一处理
// （登出 + 提示"登录态过期，请重新登录"）
const AK_HANDSHAKE_REQUIRED = 'AK_HANDSHAKE_REQUIRED';

// 从响应里识别 binding invalid：
//  - 业务 JSON body：data.errCode === 'OAUTH_BINDING_INVALID'
//  - 也可能因 401/403 status 走到 error 分支，error.response.data 同样带 errCode
function detectBindingInvalid(payload) {
  return payload && payload.errCode === OAUTH_BINDING_INVALID;
}

function detectAkHandshakeRequired(payload) {
  return payload && payload.errCode === AK_HANDSHAKE_REQUIRED;
}

// 从请求 config 反解出本次签名实际使用的 SecretId。
// 业务请求把 SecretId 信息放在以下位置之一：
//  - params.SecretId（cloudapi 系列：commonParams 里有 SecretId）
//  - data.SecretId（少量 POST 形式）
//  - params.Authorization 或 data.Authorization 里的 q-ak=（COS 风格签名）
// 没有签名信息（如 oauth.js 调 /api/oauth/*、importProject 外部 apigw 等）→ 返回 undefined
function extractSecretIdFromConfig(config) {
  if (!config) return undefined;
  const params = config.params || {};
  const data = (typeof config.data === 'object' && config.data) || {};
  if (params.SecretId) return params.SecretId;
  if (data.SecretId) return data.SecretId;
  const auth = params.Authorization || data.Authorization || '';
  if (typeof auth === 'string' && auth) {
    const seg = auth.split('&').find(s => s.indexOf('q-ak=') === 0);
    if (seg) return seg.slice(5);
  }
  return undefined;
}

// 请求拦截器：统一注入 OAuth binding header。
// 接入层 validate-oauth-binding 中间件需要请求带上对应签名 sig 的 header。
// 设计：从 config 反解出本次签名用的 SecretId → 按 SecretId 查 bindingMap →
// 拿到对应 sig 注入。这样 sig 与签名来自同一次续期产物，并发安全。
// 未命中（无签名 / 永久 AK / 兼容期）→ 静默跳过，不阻塞请求。
axios.interceptors.request.use(config => {
  try {
    const url = (config && config.url) || '';
    const isAbsolute = /^https?:\/\//.test(url);
    // 绝对 URL 必须命中 cosbrowserServerUrl 才注入；相对 URL 默认走 baseURL，
    // baseURL 就是 cosbrowserServerUrl，必然是内部请求
    if (isAbsolute && url.indexOf(store.cosbrowserServerUrl) !== 0) return config;

    const secretId = extractSecretIdFromConfig(config);
    if (!secretId) return config;
    const binding = user.getBindingBySecretId && user.getBindingBySecretId(secretId);
    if (!binding || !binding.sig || !binding.expireAt) return config;

    config.headers = config.headers || {};
    config.headers['x-cos-binding-sig'] = binding.sig;
    config.headers['x-cos-binding-expire'] = String(binding.expireAt);
  } catch (e) {
    // 防御性：interceptor 任何异常都不影响请求继续
    console.warn('[binding header inject] axios request interceptor error', e);
  }
  return config;
});

// 添加响应拦截器
axios.interceptors.response.use(
  response => {
    const { data, status } = response;
    const statusSuccess = Math.floor(status / 100) === 2; // 200 202 204 206
    if (statusSuccess) {
      if (!data.code) {
        return data.data || data;
      }
      // 业务错误。如果是 access-rules 拦截，单独发事件给 App.vue 兜底处理
      if (data && ACCESS_BLOCKED_ERR_CODES.has(data.errCode)) {
        eventProxy.emit('access-blocked', {
          errCode: data.errCode,
          message: data.message,
          blockedAuth: data.blockedAuth,
          currentAuth: data.currentAuth,
        });
      } else if (detectBindingInvalid(data)) {
        // OAuth binding 校验失败：交 App.vue 统一处理（登出 + 跳登录页 + 提示重登）
        eventProxy.emit('oauth-binding-invalid', { errCode: data.errCode });
      } else if (detectAkHandshakeRequired(data)) {
        // AK 白名单 ticket cookie 缺失/失效：交 App.vue 统一处理（登出 + 提示重登）
        eventProxy.emit('ak-handshake-required', { errCode: data.errCode });
      }
      return Promise.reject(data);
    }
    return Promise.reject(data);
  },
  error => {
    if (error.code) {
      return Promise.reject(error);
    }
    // 后端 binding-middleware 返 403 + JSON body 时走到这里：识别并触发强刷
    const respData = error && error.response && error.response.data;
    if (detectBindingInvalid(respData)) {
      eventProxy.emit('oauth-binding-invalid', { errCode: respData.errCode });
      return Promise.reject(respData);
    }
    if (detectAkHandshakeRequired(respData)) {
      eventProxy.emit('ak-handshake-required', { errCode: respData.errCode });
      return Promise.reject(respData);
    }
    const err = { code: -1, message: '网络繁忙，请稍候再试' };
    return Promise.reject(err);
  },
);

const tools = {
  getCloudApiHost(name) {
    return `${name}.tencentcloudapi.com`;
  },
};

/**
 * 列出文件列表
 * @param {*} bucketInfo
 * @param {*} query
 */
async function listObjects(bucketInfo, query) {
  // 默认值
  if (!query.MaxKeys) {
    query.MaxKeys = 200;
  }
  if (!query.Marker) {
    query.Marker = '';
  }
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;

  const authParams = {
    SecretId,
    SecretKey,
    Method: 'GET',
    Query: {
      prefix: query.Prefix,
      marker: query.Marker,
      delimiter: query.Delimiter,
      'max-keys': query.MaxKeys,
    },
  };
  if (!query.Delimiter) {
    delete authParams.Query.delimiter;
  }
  let Authorization = cos.getAuth(authParams);
  if (credential.token) {
    Authorization += `&x-cos-security-token=${credential.token}`;
  }
  const params = {
    ...bucketInfo,
    ...query,
    Authorization,
  };
  return axios.get('/cos/object/list', { params }).then(res => {
    const { ListBucketResult } = res;

    let Contents = ListBucketResult.Contents || [];
    let CommonPrefixes = ListBucketResult.CommonPrefixes || [];

    Contents = isArray(Contents) ? Contents : [Contents];
    CommonPrefixes = isArray(CommonPrefixes) ? CommonPrefixes : [CommonPrefixes];
    return Promise.resolve({ ...ListBucketResult, Contents, CommonPrefixes });
  });
}

/**
 * 列出存储桶列表
 * @param {Object} opt
 */
async function listBuckets() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;

  const authParams = {
    SecretId,
    SecretKey,
    Method: 'GET',
  };
  let Authorization = cos.getAuth(authParams);
  if (credential.token) {
    Authorization += `&x-cos-security-token=${credential.token}`;
  }

  const params = {
    Authorization,
  };
  return axios.get('/cos/bucket/list', { params }).then(res => {
    let buckets = (res
        && res.ListAllMyBucketsResult
        && res.ListAllMyBucketsResult.Buckets
        && res.ListAllMyBucketsResult.Buckets.Bucket)
      || [];
    buckets = isArray(buckets) ? buckets : [buckets];
    const owner = (res && res.ListAllMyBucketsResult && res.ListAllMyBucketsResult.Owner) || {};

    return Promise.resolve({
      Buckets: buckets,
      Owner: owner,
    });
  });
}

/**
 * 配饰存储桶权限 V4 双写
 * @param {Object} params
 */
async function putV4BucketAcl(params) {
  const { Bucket, Region, ACL } = params;
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;

  const authParams = {
    Version: '4.0', // 计算 V4 签名
    Bucket,
    SecretId,
    SecretKey,
  };

  let Authorization = cosAuth(authParams);
  if (credential.token) {
    Authorization += `&x-cos-security-token=${credential.token}`;
  }
  const reqParams = {
    Authorization: decodeURIComponent(Authorization),
    Bucket,
    Region,
    ACL,
  };
  return axios.post('/cos/bucket/putV4BucketAcl', reqParams);
}

/**
 * 获取用户信息
 */
async function getUserInfo() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'GetUserAppId',
    Version: '2019-01-16',
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };
  const host = tools.getCloudApiHost('cam');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cam';
  return axios.post('/cos/others/cloudapi', requestData);
}

/**
 * 获取标签列表
 * @param {Object} params
 */
async function getBucketTagList(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  // 全量拉取时data.Tags 且可能重复
  // 非全量拉去时data.Rows
  // 使用请自行斟酌
  const { isFull } = params;
  const opt = {
    // 必填参数
    Action: isFull ? 'DescribeTags' : 'DescribeResourceTags',
    Version: '2018-08-13',
    // 选填参数
    Limit: 20000,
  };

  const requestData = {
    ...commonParams,
    Region: 'ap-guangzhou',
    ...opt,
  };
  const host = tools.getCloudApiHost('tag');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'tag';

  return axios.post('/cos/others/cloudapi', requestData);
}

async function getBucketByTags(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const { Tag } = params;
  let opt = {
    // 必填参数
    Action: 'DescribeResourcesByTags',
    Version: '2018-08-13',
    TagFilters: [{ TagKey: Tag.split(';')[0], TagValue: [Tag.split(';')[1] || null] }],
    // 选填参数
    ServiceType: 'COS',
    Limit: 5000,
  };
  opt = mergeData(opt);

  const requestData = {
    ...commonParams,
    ...opt,
  };
  const host = tools.getCloudApiHost('tag');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'tag';

  return axios.post('/cos/others/cloudapi', requestData);
}

/**
 * 检查存储桶是否存在 CDN 域名
 * @param {Object} params
 */
async function isCdnHostExist(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const { Bucket, Region } = params;
  const defaultCdnDomain = `${Bucket}.file.myqcloud.com`;
  const restfulDomain = `${Bucket}.cos.${Region}.myqcloud.com`;
  const websiteDomain = `${Bucket}.cos-website.${Region}.myqcloud.com`;

  let opt = {
    Action: 'DescribeDomains',
    Version: '2018-06-06',
    Filters: [{ Name: 'origin', Value: [restfulDomain, websiteDomain] }],
  };
  opt = mergeData(opt);

  const requestData = {
    ...commonParams,
    Region: 'ap-guangzhou',
    ...opt,
  };
  const host = tools.getCloudApiHost('cdn');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cdn';

  return axios.post('/cos/others/cloudapi', requestData).then(res => {
    let isExist = false;
    isExist = _.some(res.Domains || [], item => {
      const { Domain } = item;
      return (
        // 默认加速域名未关闭 || 存在自定义加速域名
        (Domain === defaultCdnDomain && item.Status !== 'offline') || Domain !== defaultCdnDomain
      );
    });
    return Promise.resolve(isExist);
  });
}

async function getBucketCustomizeCdnHostNew(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const { Bucket, Region } = params;
  const defaultCdnDomain = `${Bucket}.file.myqcloud.com`;
  const restfulDomain = `${Bucket}.cos.${Region}.myqcloud.com`;
  const websiteDomain = `${Bucket}.cos-website.${Region}.myqcloud.com`;
  // V4版本兼容
  const V4Region = V4RegionMap[Region];
  const V4Domain = `${Bucket}.cos${V4Region}.myqcloud.com`;

  let opt = {
    Action: 'DescribeDomains',
    Version: '2018-06-06',
    Filters: [{ Name: 'origin', Value: [defaultCdnDomain, restfulDomain, websiteDomain, V4Domain] }],
  };
  opt = mergeData(opt);

  const requestData = {
    ...commonParams,
    Region: 'ap-guangzhou',
    ...opt,
  };
  const host = tools.getCloudApiHost('cdn');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cdn';
  return axios.post('/cos/others/cloudapi', requestData).then(res => {
    const Domains = res.Domains.filter(item => item.Status === 'online');
    const defaultCdnList = [];
    const customCdnList = [];
    Domains.forEach(item => {
      const _item = {
        type: 'cdn',
        domain: item.Domain,
        cname: item.Cname,
        status: item.Status,
      };
      if (item.Domain === defaultCdnDomain) {
        defaultCdnList.push(_item);
      } else {
        customCdnList.push(_item);
      }
    });

    const result = { defaultCdnList, customCdnList };
    return Promise.resolve(result);
  });
}

async function getSts(params) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'GetFederationToken',
    Version: '2018-08-13',
    Name: 'cosbrowser',
  };

  let { Region } = params;

  const { Policy, DurationSeconds } = params;
  const requestData = {
    ...commonParams,
    Region,
    ...opt,
    Policy: encodeURIComponent(Policy),
    DurationSeconds,
  };
  let host = tools.getCloudApiHost('sts');
  if (Region.indexOf('-fsi') > -1) {
    host = tools.getCloudApiHost(`sts.${Region}`);
  } else {
    Region = 'ap-guangzhou';
  }
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  if (Region === 'ap-guangzhou') {
    requestData.Product = 'sts';
  } else {
    requestData.Product = `sts.${Region}`;
  }
  return axios.post('/cos/others/cloudapi', requestData);
}

async function importProject(params) {
  const {
    Bucket, Region, Prefix, url, sid, skey, token,
  } = params;
  const data = {
    bucket: Bucket,
    region: Region,
    prefix: Prefix,
    fileUrl: url,
    sid,
    skey,
    token,
  };
  const options = {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    data: queryParamsToString(data),
    url: 'https://service-psscj12e-1251668577.hk.apigw.tencentcs.com/import',
    timeout: 900000,
  };
  return axios(options)
    .catch(err => {
      if (err.code) {
        return Promise.reject(err);
      }
      return { code: 'RequestError' };
    });
}

function getStsWhileImport(filename) {
  return axios({
    method: 'GET',
    url: '/api/import/getSts',
    params: { filename },
  }).catch(err => {
    if (err.error) {
      return Promise.reject(err.error);
    }
    const error = { code: 'RequestError' };
    return Promise.reject(error);
  });
}

async function getService() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    // 必填参数
    Action: 'ListBuckets',
    Version: '2021-02-24',
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };

  const host = tools.getCloudApiHost('cos');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cos';

  return axios.post('/cos/others/cloudapi', requestData);
}

/**
 * 获取收藏存储桶列表
 */
async function listAccessPaths() {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'ListAccessPaths',
    Version: '2021-02-24',
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };

  const host = tools.getCloudApiHost('cos');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cos';

  return axios.post('/cos/others/cloudapi', requestData);
}

/**
 * 添加收藏存储桶
 */
async function addAccessPath(data) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'AddAccessPath',
    Version: '2021-02-24',
    AccessPath: data.path,
    CosRegion: data.region,
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };

  const host = tools.getCloudApiHost('cos');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cos';

  return axios.post('/cos/others/cloudapi', requestData);
}

/**
 * 删除收藏存储桶
 */
async function deleteAccessPath(data) {
  const credential = await user.getCredential();
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const commonParams = getQcloudApiCommonParams(SecretId);

  const opt = {
    Action: 'DeleteAccessPath',
    Version: '2021-02-24',
    AccessPath: data.path,
    CosRegion: data.region,
  };

  const requestData = {
    ...commonParams,
    ...opt,
  };

  const host = tools.getCloudApiHost('cos');
  if (credential.token) {
    requestData.Token = credential.token;
  }
  requestData.Signature = getQcloudSign('POST', requestData, SecretKey, host);
  requestData.Product = 'cos';

  return axios.post('/cos/others/cloudapi', requestData);
}

export default {
  listObjects,
  listBuckets,
  putV4BucketAcl,
  getUserInfo,
  getBucketTagList,
  getBucketByTags,
  isCdnHostExist,
  getBucketCustomizeCdnHostNew,
  getSts,
  getService,
  addCosBrowserTask: params => axios.post('/cosbrowser/api/task/add', params),
  checkCosBrowserTask: params => axios.get('/cosbrowser/api/task/check', { params }),
  saveSharedFilesList: params => axios.post('/api/share/save', params),
  getWhiteList: params => axios.post('/api/user/whitelist', params),
  // 查 access-rules 体系下被禁用的认证方式
  // 后端：cosbrowser-server routes/api/access-rules.js
  //  - 传 appId：返回该 appId 命中的规则（match:'*' + match.appId=xxx 并集）
  //  - 不传 appId（登录页等匿名场景）：只返回 match:'*' 通配规则结果
  getAccessRulesMe: appId => {
    const params = {};
    if (appId) params.appId = appId;
    return axios.get('/api/access-rules/me', { params });
  },
  /**
   * AK 白名单登录握手
   *
   * 前端本地用 SK 签好 cam.GetUserAppId 请求（SK 不出浏览器），发给接入层校验。
   * 接入层：透传到 cam 拿真 AppId → 与前端声明的 appid 比对 → 一致才签发 cookie。
   *
   * 仅供 ?loginType=ak&appid=X 白名单私链登录场景使用，普通用户不会走此接口。
   *
   * @param {object} payload
   * @param {string} payload.appid           前端 URL 上声明的 appid
   * @param {object} payload.signedCamRequest 前端本地签好的 cam.GetUserAppId 请求
   */
  postAkLoginHandshake: ({ appid, signedCamRequest }) => axios.post(
    '/api/ak-login/handshake',
    { appid, signedCamRequest },
  ),
  importProject,
  getStsWhileImport,
  listAccessPaths,
  addAccessPath,
  deleteAccessPath,
};
